import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';
import { ERROR_MESSAGES } from '../shared/constants/error-message.constants';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { HttpRequestService } from '../shared/services/http-request.service';
import { NotificationService } from '../shared/services/notification.service';
import { formatUrl } from '../shared/utils/format-url-util';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationsDetailsService {

  private authDocUpload$ = new BehaviorSubject<any>(false);
  private addMoreAuth$ = new BehaviorSubject<any>(false);
  private clonedAuthorization$ = new BehaviorSubject<any>(null);

  constructor(
    private http: HttpRequestService,
    private errorHandler: GlobalErrorHandlerService,
    private notificationService : NotificationService
  ) {}

  setDocUpload(newStatus: boolean): void {
    this.authDocUpload$.next(newStatus)
  }

  getDocUpload(): Observable<any> {
    return this.authDocUpload$.asObservable();
  }

  setAddMoreAuth(newStatus: boolean): void {
    this.addMoreAuth$.next(newStatus)
  }

  getAddMoreAuth(): Observable<any> {
    return this.addMoreAuth$.asObservable();
  }

  // Emit the reset on the same subject (rather than replacing it) so existing subscribers see it
  // and later subscribers don't get a stale value replayed.
  clearAddMoreAuth() {
    this.addMoreAuth$.next(false);
  }

  setClonedAuthorization(data: any): void {
    this.clonedAuthorization$.next(data)
  }

  getClonedAuthorization(): Observable<any> {
    return this.clonedAuthorization$.asObservable();
  }

  clearClonedAuthorization(){
    this.clonedAuthorization$.next(null);
  }

  updateAuthorizationDetails(program_code: string, authorization_id: string, data: any): any {
    console.log('lead_id', authorization_id);
    const url = formatUrl(
       ENDPOINTS.UPDATE_AUTHORIZATION_DETAILS,
      { program_code, authorization_id }
    );
    console.log(url);
    return this.http.patch(url, data).pipe(
        tap(() => { 
          this.notificationService.success(
            'Success',
            'Successfully updated Authorization'
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );

  }

  /**
   * Authorizations, provider allocations and service details of one spending plan —
   * the expandable rows of the spending plans list.
   */
  getSpendingPlanDetails(program_code: string, participantPplId: string, clientAuthNumber: string, serviceCoordinator: string, regionalCenter: string ): any {
    const url = formatUrl(ENDPOINTS.GET_SPENDING_PLAN_DETAILS, { program_code });
    return this.http.post(url, { participantPplId, clientAuthNumber, serviceCoordinator, regionalCenter }).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  saveAuthorization(program_code: string,data: any, rfrwRequest = false) {
    const url = formatUrl(ENDPOINTS.SAVE_AUTHORIZATIONS, {program_code});
    return this.http.post(url, data, rfrwRequest).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  addUpdateAllocation(program_code: string, data: any, action: any, authorizationProviderId: any, rfrwRequest = false) {
    let url;
    if(action === 'Add'){
      url = formatUrl( ENDPOINTS.ADD_ALLOCATION, { program_code } );
    } else {
      url = formatUrl( ENDPOINTS.UPDATE_ALLOCATION, { program_code, authorizationProviderId } );
    }
    
    return this.http.post(url, data, rfrwRequest).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  deleteAuthorization(program_code: string, authorizationId: any) {
    const url = formatUrl(ENDPOINTS.DELETE_AUTHORIZATION, {program_code, authorizationId});
    return this.http
    .delete(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  deactivateAllocation(program_code: string, authorizationProviderId: any) {
    const url = formatUrl(ENDPOINTS.DEACTIVATE_AUTHORIZATION, {program_code, authorizationProviderId});
    return this.http
    .delete(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  addUpdateServiceDetail(program_code: string, data: any, action: any, serviceDetailId: any) {
    let url;
    if (action === 'Add') {
      url = formatUrl(ENDPOINTS.ADD_AUTH_SERVICE_DETAIL, { program_code });
    } else {
      url = formatUrl(ENDPOINTS.UPDATE_AUTH_SERVICE_DETAIL, { program_code, serviceDetailId });
    }

    return this.http.post(url, data, true).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getServiceDetail(program_code: string, serviceDetailId: any) {
    const url = formatUrl(ENDPOINTS.GET_AUTH_SERVICE_DETAIL, { program_code, serviceDetailId });
    return this.http.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  deactivateServiceDetail(program_code: string, serviceDetailId: any) {
    const url = formatUrl(ENDPOINTS.DEACTIVATE_AUTH_SERVICE_DETAIL, { program_code, serviceDetailId });
    return this.http.delete(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }
}
